export default class DeliverableJobsWebService {
    constructor($sbRequest, API_VERSION) {
        "ngInject";

        this.$sbRequest = $sbRequest;
        this.API_VERSION = API_VERSION;

        this.ASSIGN_STRATEGY = {
            OVERWRITE: "OVERWRITE",
            SAFE_OVERWRITE: "SAFE_OVERWRITE",
            UPDATE: "UPDATE",
            ASSOCIATE: "ASSOCIATE",
        };
    }

    /**
     * POST request to create a deliverable job with given type and definition
     *
     * @param projectId {string}            UUID
     *
     * @param body {Object}                 Request body
     * @param body.type {string}            Name of the job
     * @param body.definition {Object}      Detailed description about the job setup
     *
     * @returns {Promise<{}>}
     */
    create(projectId, { type, definition }) {
        const endpointUrl = `/api/${this.API_VERSION}/api/projects/${projectId}/deliverable-jobs`;
        return this.$sbRequest({
            url: endpointUrl,
            method: "POST",
            body: JSON.stringify({
                type,
                definition,
            }),
            header: {
                "Content-Type": "application/json",
            },
        }).then((resp) => {
            const jobId = resp.getResponseHeader("x-job-id");
            const response = JSON.parse(resp.responseText);
            return { response, jobId };
        });
    }

    /**
     * Create an 'IMPORT' deliverable job
     *
     * @param projectId {string}              UUID
     *
     * @param body {Object}                   Request body
     * @param body.deliverables {Object[]}    List all deliverables that are supposed to be created
     * @param body.templateId   {string}      Optional process template to be assigned right on deliverable creation
     *
     * @returns {Promise<{}>}
     */
    createImportJob(projectId, { deliverables, templateId }) {
        const definition = {
            deliverables,
        };

        if (templateId) {
            definition.assign_template = {
                process_template_id: templateId,
                strategies: ["UPDATE", "ASSOCIATE"],
            };
        }

        return this.create(projectId, {
            type: "IMPORT",
            definition,
        });
    }

    /**
     * Create a 'MODIFY_PROPERTIES' deliverable job
     *
     * @param projectId {string}                UUID
     *
     * @param body {Object}                     Request body
     * @param body.properties {Object}          Definition of the change
     * @param body.deliverable_set {string|string[]}    Filter expression list of deliverable ids to identify a set of deliverables. All those deliverables will be updated
     *
     * @returns {Promise<{}>}
     */
    createModifyPropertiesJob(projectId, { properties, deliverable_set }) {
        return this.create(projectId, {
            type: "MODIFY_PROPERTIES",
            definition: {
                properties,
                deliverable_set,
            },
        });
    }

    /**
     * Create an 'ASSIGN_PROCESS' deliverable job
     *
     * @param projectId {string}                    UUID
     *
     * @param body {Object}                             Request body
     * @param body.strategy {string}                    Strategy of assignment
     * @param body.deliverable_set {string|string[]}    Filter expression list of deliverable ids to identify a set of deliverables. All those deliverables will be updated
     * @param body.process_template_id {string}         The unique platform wide identifier
     *
     * @returns {Promise<{}>}
     */
    createAssignProcessJob(
        projectId,
        { strategy, deliverable_set, process_template_id }
    ) {
        return this.create(projectId, {
            type: "ASSIGN_PROCESS",
            definition: {
                strategy,
                deliverable_set,
                process_template_id,
            },
        });
    }

    /**
     * Create a 'REMOVE' deliverable job
     *
     * @param projectId {string}                  UUID
     *
     * @param body {Object}                       Request body
     * @param body.deliverable_set {string|string[]}    Filter expression list of deliverable ids to identify a set of deliverables. All those deliverables will be updated
     *
     * @returns {Promise<{}>}
     */
    createRemoveJob(projectId, { deliverable_set }) {
        return this.create(projectId, {
            type: "REMOVE",
            definition: {
                deliverable_set,
            },
        });
    }
}
